import type { ProvisioningTargetStatus } from '../servicebus/template-provisioning';
import type { BaseDocument } from '../mongo/base-document.interface';

// ─── Status ───────────────────────────────────────────────────────────────────

export const TEMPLATE_CONFIGURATION_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  INACTIVE: 'inactive',
  PENDING: 'pending',
} as const;

export type TemplateConfigurationStatus =
  (typeof TEMPLATE_CONFIGURATION_STATUS)[keyof typeof TEMPLATE_CONFIGURATION_STATUS];

/**
 * Allowed transitions: pending→active, active→inactive, inactive→active, any→archived.
 */
export const TEMPLATE_CONFIGURATION_STATUS_TRANSITIONS: Record<
  TemplateConfigurationStatus,
  TemplateConfigurationStatus[]
> = {
  [TEMPLATE_CONFIGURATION_STATUS.ACTIVE]: [
    TEMPLATE_CONFIGURATION_STATUS.INACTIVE,
    TEMPLATE_CONFIGURATION_STATUS.ARCHIVED,
  ],
  [TEMPLATE_CONFIGURATION_STATUS.ARCHIVED]: [],
  [TEMPLATE_CONFIGURATION_STATUS.INACTIVE]: [
    TEMPLATE_CONFIGURATION_STATUS.ACTIVE,
    TEMPLATE_CONFIGURATION_STATUS.ARCHIVED,
  ],
  [TEMPLATE_CONFIGURATION_STATUS.PENDING]: [
    TEMPLATE_CONFIGURATION_STATUS.ACTIVE,
    TEMPLATE_CONFIGURATION_STATUS.ARCHIVED,
  ],
};

// ─── Provisioning State ───────────────────────────────────────────────────────

export interface ProvisioningTargetState {
  error?: string;
  status: ProvisioningTargetStatus;
}

export interface TemplateConfigurationProvisioning {
  incoming?: ProvisioningTargetState;
  outgoing?: ProvisioningTargetState;
}

// ─── Entity ───────────────────────────────────────────────────────────────────

export interface TemplateConfigurationProvisioningError {
  error?: string;
  target: string;
}

export interface TemplateConfiguration extends BaseDocument {
  /**
   * Shipper code identifying which shipper instance this config belongs to.
   * Set at install time; used for per-shipper routing isolation.
   */
  shipperCode?: string;
  provisioning?: TemplateConfigurationProvisioning;
  provisioningError?: TemplateConfigurationProvisioningError;
  status: TemplateConfigurationStatus;
  templateId: string;
  values: Record<string, unknown>;
}

// ─── CRUD Types ───────────────────────────────────────────────────────────────

export type CreateTemplateConfiguration = {
  templateId: string;
  values: Record<string, unknown>;
};

export type UpdateTemplateConfiguration = {
  values: Record<string, unknown>;
};

export type UpdateTemplateConfigurationStatus = {
  status: TemplateConfigurationStatus;
};

// ─── Resolved Refs ───────────────────────────────────────────────────────────

/**
 * Lightweight display metadata for a resolved template reference.
 * Only carries the fields needed to render a row — no blob content.
 */
export interface ResolvedTemplateRef {
  id: string;
  logo: string;
  name: string;
}

/**
 * TemplateConfiguration enriched with resolved template display metadata.
 * Mirrors the VehicleWithResolvedRefs pattern: the resolved ref is always
 * present on the list endpoint. When the template cannot be resolved,
 * `name` falls back to the templateId and `logo` to an empty string so
 * the row still renders without omitting the field.
 */
export interface TemplateConfigurationWithResolvedRefs extends TemplateConfiguration {
  resolvedTemplate: ResolvedTemplateRef;
}

// ─── Search Criteria ─────────────────────────────────────────────────────────

export interface SearchTemplateConfigurationCriteria {
  statuses?: TemplateConfigurationStatus[];
  templateId?: string;
}
